import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/site-chrome";
import { getNews, news, categoryOf, hoursAgo, readingTime } from "@/lib/news";

export const Route = createFileRoute("/article/$id")({
  notFoundComponent: () => (
    <PageShell>
      <div className="container-page py-16 text-center">
        <h1 className="text-3xl font-bold">Article not found</h1>
        <p className="mt-3 text-muted-foreground">The story you're looking for isn't available.</p>
        <Link to="/" className="inline-block mt-6 text-sm underline">Back to football home</Link>
      </div>
    </PageShell>
  ),
  component: ArticlePage,
});

function ArticlePage() {
  const params = Route.useParams();
  const item = getNews(params.id);

  useEffect(() => {
    if (item) {
      document.title = `${item.title} — Weborbis Sport`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute("content", item.excerpt);
      }
    } else {
      document.title = "Article not found — Weborbis Sport";
    }
  }, [item]);

  if (!item) {
    return null;
  }

  const paragraphs = item.content.split(/\n\n+/);
  const related = news.filter((n) => n.id !== item.id).slice(0, 4);

  return (
    <PageShell>
      <article className="container-page py-6 max-w-none lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
        <div className="min-w-0">
          <nav className="text-xs text-muted-foreground mb-3">
            <Link to="/" className="hover:underline">Football</Link>
            <span className="mx-1">›</span>
            <span>{categoryOf(item.title)}</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-extrabold leading-tight">{item.title}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-block bg-brand text-brand-foreground px-2 py-0.5 rounded-sm font-semibold">
              {categoryOf(item.title)}
            </span>
            <span>{hoursAgo(item.id)}h ago</span>
            <span>· {readingTime(item.wordCount)} min read</span>
            <span>· By Weborbis Sport Desk</span>
          </div>

          <figure className="mt-6 overflow-hidden rounded-md border border-border bg-muted">
            <img src={item.image} alt={item.title} className="w-full h-auto object-cover" />
            <figcaption className="px-3 py-2 text-xs text-muted-foreground bg-secondary">
              Source: {item.Reference}
            </figcaption>
          </figure>

          <div className="prose prose-neutral max-w-none mt-6">
            {paragraphs.map((p: string, i: number) => (
              <p key={i} className="leading-relaxed text-[15.5px] text-foreground/90 mb-5">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-8 border-t border-border pt-4 text-xs text-muted-foreground">
            <div>
              Reference:{" "}
              <a
                href={item["Reference website"]}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="underline"
              >
                {item["Reference website"]}
              </a>
            </div>
            <div className="mt-1">Word count: {item.wordCount}</div>
          </div>
        </div>

        <aside className="mt-10 lg:mt-0">
          <div className="border-t-4 border-ink pt-2 mb-4">
            <h2 className="text-sm font-bold">More Football</h2>
          </div>
          <ul className="space-y-4">
            {related.map((r) => (
              <li key={r.id}>
                <Link
                  to="/article/$id"
                  params={{ id: r.id }}
                  className="group flex gap-3"
                >
                  <div className="w-24 aspect-[16/10] shrink-0 overflow-hidden rounded bg-muted">
                    <img src={r.image} alt={r.title} className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold leading-snug group-hover:underline line-clamp-3">
                      {r.title}
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {hoursAgo(r.id)}h ago · {categoryOf(r.title)}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </aside>
      </article>
    </PageShell>
  );
}
